// src/pages/AddictionDetection.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/AddictionDetection.css";
import Navbar from "../components/Navbar";

const PAGE_SIZE = 5;
const LS_KEY = (userId) => `addictionDetectionProgress:${userId}`;

const AddictionDetection = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [questions, setQuestions] = useState([]);
  const [userData, setUserData] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const navigate = useNavigate();

  // 🔹 Guardar progreso en localStorage
  const persistLocal = (draft = {}) => {
    if (!userData?._id) return;
    const payload = {
      responses: draft.responses ?? responses,
      currentStep: draft.currentStep ?? currentStep,
      updatedAt: new Date().toISOString(),
      version: 1,
    };
    localStorage.setItem(LS_KEY(userData._id), JSON.stringify(payload));
  };

  // 🔹 Obtener datos del usuario + cargar preguntas
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/me`, {
          headers: { Authorization: token },
        });
        setUserData(response.data);
      } catch (error) {
        console.error("Error al obtener datos del usuario:", error);
      }
    };

    fetchUserData();
    
    setQuestions([
      "¿Con qué frecuencia consumes bebidas alcohólicas?",
      "¿Con qué frecuencia consumes cigarrillos o productos de tabaco?",
      "¿Has probado drogas recreativas en el último año?",
      "¿Sientes la necesidad de consumir alguna sustancia para aliviar el estrés?",
      "¿Consumes sustancias para mejorar tu estado de ánimo?",
      "¿Te sientes presionado por amigos o familiares a consumir alguna sustancia?",
      "¿Has consumido sustancias de forma oculta o en secreto?",
      "¿Te resulta difícil limitar la cantidad que consumes?",
      "¿Has intentado dejar de consumir alguna sustancia sin éxito?",
      "¿Sientes que necesitas consumir más para obtener el mismo efecto?",
      "¿Te preocupa el gasto de dinero en sustancias?",
      "¿Has descuidado obligaciones (estudio, trabajo) por consumir?",
      "¿Sientes culpa o remordimiento después de consumir?",
      "¿Has tenido conflictos familiares o con amigos por tu consumo?",
      "¿Utilizas sustancias para enfrentar problemas personales?",
      "¿Te sientes ansioso o irritable cuando no consumes?",
      "¿Prefieres consumir solo para evitar ser juzgado?",
      "¿Has perdido el control sobre la cantidad consumida?",
      "¿Has sentido que tu consumo afecta negativamente tus relaciones interpersonales?",
      "¿Te has sentido avergonzado por tu consumo?",
      "¿Has experimentado síntomas físicos relacionados con el consumo?",
      "¿Has utilizado sustancias para evadir responsabilidades o problemas?",
      "¿Consideras que el consumo te afecta en el rendimiento académico o laboral?",
      "¿Sientes que tu consumo ha aumentado en el último año?",
      "¿Has ocultado tu consumo a personas importantes para ti?",
      "¿Te sientes solo y recurres a sustancias para sentirte acompañado?",
      "¿Has recibido sugerencias de amigos o familiares para reducir tu consumo?",
      "¿Te preocupa la imagen que proyectas debido a tu consumo?",
      "¿Has experimentado episodios de pérdida de control emocional al consumir?",
      "¿Te has alejado de actividades que antes disfrutabas por consumir?",
      "¿Sientes que el consumo afecta tu salud física de forma visible?",
      "¿Has tenido dificultades para concentrarte debido al consumo?",
      "¿Crees que el consumo ha cambiado tu forma de relacionarte con los demás?",
      "¿Sientes ansiedad o preocupación sobre el futuro de tu salud por el consumo?",
      "¿Te gustaría recibir ayuda profesional para mejorar tu situación actual?"
    ]);
  }, []);

  // 🔹 Cargar progreso guardado
  useEffect(() => {
    if (!userData?._id) return;
    try {
      const raw = localStorage.getItem(LS_KEY(userData._id));
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.responses) setResponses(saved.responses);
        if (Number.isInteger(saved?.currentStep)) setCurrentStep(saved.currentStep);
      }
    } catch (e) {
      console.warn("No se pudo cargar el progreso local:", e);
    }
  }, [userData?._id]);

  const steps = Math.ceil(questions.length / PAGE_SIZE);

  const handleChange = (e, index) => {
    const next = { ...responses, [index]: e.target.value };
    setResponses(next);
    persistLocal({ responses: next });
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prev) => {
        const next = prev + 1;
        persistLocal({ currentStep: next });
        return next;
      });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrev = () => {
    setCurrentStep((prev) => {
      const next = Math.max(prev - 1, 0);
      persistLocal({ currentStep: next });
      return next;
    });
  };

  const validateStep = () => {
    const start = currentStep * PAGE_SIZE;
    const end = Math.min(start + PAGE_SIZE, questions.length);
    for (let i = start; i < end; i++) {
      if (!responses[i]) {
        alert("Por favor, responde todas las preguntas antes de continuar.");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    if (!token) {
      alert("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
      navigate("/login");
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/submit-addiction`,
        { responses },
        { headers: { Authorization: token } }
      );

      if (response.status === 200) {
        alert("Encuesta enviada con éxito");
        if (userData?._id) localStorage.removeItem(LS_KEY(userData._id));
        navigate("/dashboard");
      } else {
        alert("Hubo un problema al enviar la encuesta");
      }
    } catch (error) {
      console.error("Error al enviar la encuesta:", error);
      alert("Ocurrió un error. Intenta de nuevo.");
    }
  };

  const handleSaveAndExit = () => {
    persistLocal();
    alert("Progreso guardado localmente. Podrás continuar luego desde este equipo.");
    navigate("/dashboard");
  };

  const toggleSidebar = () => setShowSidebar(!showSidebar);
  const closeSidebar = () => setShowSidebar(false);

  return (
    <>
      <Navbar />
      
      <div className="dashboard-layout">
        <div className={`sidebar-overlay ${showSidebar ? "show" : ""}`} onClick={closeSidebar}></div>

        <div className={`dashboard-sidebar ${showSidebar ? "show" : ""}`}>
          <div className="sidebar-header">
            <h3>Detección de Adicciones</h3>
          </div>

          <div className="sidebar-menu">
            <div className="survey-progress">
              <div className="progress-info">
                <span>Progreso</span>
                <span>{currentStep + 1}/{steps}</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${((currentStep + 1) / steps) * 100}%` }} />
              </div>
            </div>
          </div>

          <button 
            className="menu-item return-dashboard"
            onClick={() => {
              if(window.confirm("¿Estás seguro de que deseas volver al dashboard? Tus respuestas se han guardado y podrás continuar luego.")) {
                handleSaveAndExit();
              }
            }}
          >
            <i className="bi bi-arrow-left-circle"></i>
            Volver al Dashboard
          </button>
        </div>

        <div className="dashboard-main">
          <div className="survey-container">
            <div className="survey-header">
              <h2>Evaluación de Detección de Adicciones</h2>
              <p className="survey-description">
                Esta evaluación nos ayudará a identificar si tienes problemas de adicciones.
              </p>
              <div className="button-container">
                <button 
                  type="button" 
                  className="nav-button prev"
                  onClick={handleSaveAndExit}
                >
                  <i className="bi bi-floppy"></i>
                  Guardar y salir
                </button>
              </div>
            </div>

            <form className="survey-form" onSubmit={handleSubmit}>
              <div className="questions-group">
                {questions.slice(currentStep * PAGE_SIZE, (currentStep + 1) * PAGE_SIZE).map((question, index) => (
                  <div className="question-card" key={index + currentStep * PAGE_SIZE}>
                    <div className="question-header">
                      <span className="question-number">Pregunta {index + 1 + currentStep * PAGE_SIZE}</span>
                      <p className="question-text">{question}</p>
                    </div>

                    <div className="options-grid">
                      {[
                        { value: "0", label: "Nunca" },
                        { value: "1", label: "Rara vez" },
                        { value: "2", label: "A veces" },
                        { value: "3", label: "Frecuentemente" }
                      ].map((option) => (
                        <label 
                          key={option.value}
                          className={`option-card ${responses[index + currentStep * PAGE_SIZE] === option.value ? "selected" : ""}`}
                        >
                          <input
                            type="radio"
                            name={`q${index + currentStep * PAGE_SIZE}`}
                            value={option.value}
                            checked={responses[index + currentStep * PAGE_SIZE] === option.value}
                            onChange={(e) => handleChange(e, index + currentStep * PAGE_SIZE)}
                            required
                          />
                          <span className="option-text">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="navigation-controls">
                {currentStep > 0 && (
                  <button type="button" className="nav-button prev" onClick={handlePrev}>
                    <i className="bi bi-arrow-left"></i> Anterior
                  </button>
                )}
                {currentStep < steps - 1 ? (
                  <button type="button" className="nav-button next" onClick={handleNext}>
                    Siguiente <i className="bi bi-arrow-right"></i>
                  </button>
                ) : (
                  <button type="submit" className="nav-button submit">
                    Finalizar <i className="bi bi-check-circle"></i>
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddictionDetection;